import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import { AvatarIniciais, BotaoAcao, CampoBusca, Cartao, Etiqueta, Pagina } from "./ui/Painel";
import { CORES, normalizar } from "../utils/participante";

const CHAVE_EXCLUIDOS = "sorteio_excluidos";
const CHAVE_GANHADORES = "sorteio_ganhadores";

const carregarSet = (chave) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(chave) || "[]"));
  } catch {
    return new Set();
  }
};

const Sorteio = () => {
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [excluidos, setExcluidos] = useState(() => carregarSet(CHAVE_EXCLUIDOS));
  const [ganhadores, setGanhadores] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CHAVE_GANHADORES) || "[]");
    } catch {
      return [];
    }
  });
  const [sorteando, setSorteando] = useState(false);
  const [ganhadorAtual, setGanhadorAtual] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/participantes/listar-todos`);
        setParticipantes(data.filter((p) => p.presenca === 1));
      } catch (error) {
        setErro("Erro ao carregar convidados que compareceram.");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchDados();
  }, []);

  useEffect(() => {
    localStorage.setItem(CHAVE_EXCLUIDOS, JSON.stringify([...excluidos]));
  }, [excluidos]);

  useEffect(() => {
    localStorage.setItem(CHAVE_GANHADORES, JSON.stringify(ganhadores));
  }, [ganhadores]);

  const idsGanhadores = useMemo(() => new Set(ganhadores.map((g) => g.id)), [ganhadores]);

  const elegiveis = useMemo(
    () => participantes.filter((p) => !excluidos.has(p.id) && !idsGanhadores.has(p.id)),
    [participantes, excluidos, idsGanhadores]
  );

  const listaFiltrada = useMemo(() => {
    const termo = normalizar(filtro).trim();
    return participantes
      .filter((p) => !termo || normalizar(p.nome).includes(termo))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [participantes, filtro]);

  const toggleExcluido = (id) => {
    setExcluidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const sortear = () => {
    if (elegiveis.length === 0) return;
    setSorteando(true);
    setGanhadorAtual(null);

    setTimeout(() => {
      const escolhido = elegiveis[Math.floor(Math.random() * elegiveis.length)];
      setGanhadorAtual(escolhido);
      setGanhadores((prev) => [...prev, { id: escolhido.id, nome: escolhido.nome }]);
      setSorteando(false);
      setModalAberto(true);
    }, 900);
  };

  const reiniciarTudo = () => {
    if (!window.confirm("Isso limpa a lista de ganhadores e exclusões deste sorteio. Continuar?")) return;
    setExcluidos(new Set());
    setGanhadores([]);
    setGanhadorAtual(null);
  };

  return (
    <Pagina
      eyebrow="Evento"
      titulo="Sorteio"
      descricao="Sorteia entre os convidados que fizeram check-in. Desmarque quem não deve participar."
      acoes={
        <BotaoAcao icon={<RestartAltIcon />} onClick={reiniciarTudo} disabled={loading}>
          Reiniciar sorteio
        </BotaoAcao>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={34} sx={{ color: CORES.navy }} />
        </Box>
      ) : erro ? (
        <Alert severity="error">{erro}</Alert>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, alignItems: "start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
              sx={{
                borderRadius: "12px",
                background: "radial-gradient(circle at 50% 0%, #1d3349 0%, #0d1824 75%)",
                color: "#fff",
                px: 3,
                py: 4,
                textAlign: "center",
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 36, color: "#f2c14e" }} />
              <Typography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1.1, mt: 1 }}>{elegiveis.length}</Typography>
              <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
                {elegiveis.length === 1 ? "pessoa concorrendo" : "pessoas concorrendo"}
              </Typography>

              {ganhadorAtual && (
                <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.75)", mt: 2 }}>
                  Último sorteado: <strong style={{ color: "#fff" }}>{ganhadorAtual.nome}</strong>
                </Typography>
              )}

              <Button
                onClick={sortear}
                disabled={sorteando || elegiveis.length === 0}
                startIcon={sorteando ? <CircularProgress size={16} sx={{ color: CORES.navy }} /> : null}
                sx={{
                  mt: 3,
                  px: 5,
                  height: 44,
                  fontSize: 15,
                  fontWeight: 700,
                  backgroundColor: "#fff",
                  color: CORES.navy,
                  "&:hover": { backgroundColor: "#e9eef4" },
                  "&.Mui-disabled": { backgroundColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.5)" },
                }}
              >
                {sorteando ? "Sorteando..." : "Sortear"}
              </Button>
            </Box>

            <Cartao titulo={`Já sorteados (${ganhadores.length})`} semPadding>
              {ganhadores.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: CORES.textoSuave, px: 2.25, py: 2 }}>
                  Ninguém foi sorteado ainda.
                </Typography>
              ) : (
                ganhadores.map((g, i) => (
                  <Box
                    key={g.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 2.25,
                      py: 1.1,
                      borderTop: i === 0 ? "none" : `1px solid ${CORES.borda}`,
                    }}
                  >
                    <Typography sx={{ width: 20, fontSize: 12, fontWeight: 700, color: "#8a97a6" }}>{i + 1}º</Typography>
                    <AvatarIniciais nome={g.nome} tamanho={26} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: CORES.texto }}>{g.nome}</Typography>
                  </Box>
                ))
              )}
            </Cartao>
          </Box>

          <Cartao
            titulo="Quem participa"
            descricao={`${elegiveis.length} de ${participantes.length} que compareceram`}
            semPadding
          >
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${CORES.borda}` }}>
              <CampoBusca value={filtro} onChange={setFiltro} placeholder="Filtrar por nome" />
            </Box>
            <Box sx={{ maxHeight: 460, overflowY: "auto" }}>
              {listaFiltrada.length === 0 && (
                <Typography sx={{ fontSize: 12.5, color: CORES.textoSuave, px: 2.25, py: 2 }}>
                  {participantes.length === 0 ? "Ninguém fez check-in ainda." : "Nenhum nome encontrado."}
                </Typography>
              )}
              {listaFiltrada.map((p) => {
                const jaGanhou = idsGanhadores.has(p.id);
                return (
                  <Box
                    key={p.id}
                    component="label"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 0.25,
                      borderBottom: `1px solid ${CORES.borda}`,
                      cursor: jaGanhou ? "default" : "pointer",
                      "&:hover": { backgroundColor: jaGanhou ? "transparent" : "#fafbfc" },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={!excluidos.has(p.id) && !jaGanhou}
                      disabled={jaGanhou}
                      onChange={() => toggleExcluido(p.id)}
                    />
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: 12.5,
                        color: excluidos.has(p.id) && !jaGanhou ? "#8a97a6" : CORES.texto,
                      }}
                    >
                      {p.nome}
                    </Typography>
                    {jaGanhou && <Etiqueta tom="ambar">Já ganhou</Etiqueta>}
                  </Box>
                );
              })}
            </Box>
          </Cartao>
        </Box>
      )}

      <Dialog
        open={modalAberto}
        onClose={() => setModalAberto(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "14px", overflow: "hidden" } } }}
      >
        <Box
          sx={{
            background: "radial-gradient(circle at 50% 0%, #1d3349 0%, #0b2a45 70%)",
            px: 4,
            py: 5,
            textAlign: "center",
            position: "relative",
          }}
        >
          <IconButton
            aria-label="Fechar"
            onClick={() => setModalAberto(false)}
            sx={{ position: "absolute", top: 8, right: 8, color: "rgba(255,255,255,0.7)" }}
          >
            <CloseIcon />
          </IconButton>
          <EmojiEventsIcon sx={{ fontSize: 52, color: "#f2c14e" }} />
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 700, letterSpacing: 2, fontSize: 12, mt: 1 }}>
            GANHADOR
          </Typography>
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: { xs: 28, sm: 34 },
              lineHeight: 1.15,
              wordBreak: "break-word",
              mt: 1,
            }}
          >
            {ganhadorAtual?.nome}
          </Typography>
          <Button
            onClick={() => {
              setModalAberto(false);
              sortear();
            }}
            disabled={elegiveis.length === 0}
            sx={{
              mt: 4,
              px: 3,
              backgroundColor: "#fff",
              color: CORES.navy,
              "&:hover": { backgroundColor: "#e9eef4" },
            }}
          >
            Sortear próximo
          </Button>
        </Box>
      </Dialog>
    </Pagina>
  );
};

export default Sorteio;
